import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { neutral } from '_lex/_foundations/phxColors';
import QuickSearch from 'components/_atoms/QuickSearch';
import { translate as t } from 'hooks/useTranslations';
import ToCItem, { ToCItemData } from './ToCItem';
import { buildToCData } from '../_helpers/buildToCData';
import type { DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';

interface ToCSelectorProps {
  manual: DraftManual;
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  disabled?: boolean;
}

const ToCSelector: React.FC<ToCSelectorProps> = ({ manual, selectedIds, onSelectionChange, disabled = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  // Manual is expanded by default, chapters and prefaces are closed
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set([manual.id]));
  // Transform manual data to ToCItemData structure with numbering
  const tocData = useMemo(() => buildToCData(manual), [manual]);
  // Get all descendant IDs for an item
  const getAllDescendantIds = useCallback((item: ToCItemData): string[] => {
    const ids: string[] = [item.id];
    if (item.children) {
      item.children.forEach((child) => {
        ids.push(...getAllDescendantIds(child));
      });
    }
    return ids;
  }, []);

  // Check if an item is fully selected (all descendants selected)
  const isItemFullySelected = useCallback(
    (item: ToCItemData): boolean => {
      if (!item.children || item.children.length === 0) {
        return selectedIds.has(item.id);
      }
      return item.children.every((child) => isItemFullySelected(child));
    },
    [selectedIds]
  );

  // Check if an item is partially selected (some descendants selected)
  const isItemPartiallySelected = useCallback(
    (item: ToCItemData): boolean => {
      if (!item.children || item.children.length === 0) {
        return false;
      }
      const hasSelectedChild = item.children.some(
        (child) => isItemFullySelected(child) || isItemPartiallySelected(child)
      );
      const hasUnselectedChild = item.children.some((child) => !isItemFullySelected(child));
      return hasSelectedChild && hasUnselectedChild;
    },
    [isItemFullySelected]
  );

  // Get selection state for an item
  const getItemSelectionState = useCallback(
    (item: ToCItemData): { isSelected: boolean; isIndeterminate: boolean; } => {
      const isSelected = isItemFullySelected(item);
      const isIndeterminate = !isSelected && isItemPartiallySelected(item);
      return { isSelected, isIndeterminate };
    },
    [isItemFullySelected, isItemPartiallySelected]
  );

  // Toggle selection for an item
  const handleToggle = useCallback(
    (id: string) => {
      const findItem = (items: ToCItemData[], targetId: string): ToCItemData | null => {
        for (const item of items) {
          if (item.id === targetId) return item;
          if (item.children) {
            const found = findItem(item.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const item = id === tocData.id ? tocData : findItem(tocData.children || [], id);
      if (!item) return;

      const descendantIds = getAllDescendantIds(item);
      const newSelectedIds = new Set(selectedIds);

      const isCurrentlySelected = isItemFullySelected(item);

      if (isCurrentlySelected) {
        // Deselect all descendants
        descendantIds.forEach((descendantId) => newSelectedIds.delete(descendantId));
      } else {
        // Select all descendants
        descendantIds.forEach((descendantId) => newSelectedIds.add(descendantId));
      }

      onSelectionChange(newSelectedIds);
    },
    [tocData, selectedIds, getAllDescendantIds, isItemFullySelected, onSelectionChange]
  );

  // Toggle expand/collapse for an item
  const handleExpand = useCallback((itemId: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Filter items based on search query
  const filterItems = useCallback(
    (items: ToCItemData[]): ToCItemData[] => {
      if (!searchQuery) return items;

      const result: ToCItemData[] = [];

      for (const item of items) {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
        const filteredChildren = item.children ? filterItems(item.children) : undefined;

        if (matchesSearch || (filteredChildren && filteredChildren.length > 0)) {
          result.push({
            ...item,
            children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : item.children,
          });
        }
      }

      return result;
    },
    [searchQuery]
  );

  const filteredChildren = useMemo(() => {
    return tocData.children ? filterItems(tocData.children) : [];
  }, [tocData.children, filterItems]);

  // Recursive render function for nested items
  const renderItems = (items: ToCItemData[], depth: number) => {
    return items.map((item) => {
      const { isSelected, isIndeterminate } = getItemSelectionState(item);
      const isExpanded = expandedIds.has(item.id);

      return (
        <Box key={item.id}>
          <ToCItem
            item={item}
            isSelected={isSelected}
            isIndeterminate={isIndeterminate}
            isExpanded={isExpanded}
            onToggle={handleToggle}
            onExpand={item.hasChildren ? handleExpand : undefined}
            depth={depth}
            disabled={disabled}
          />
          {/* Render children inline if expanded */}
          {isExpanded && item.children && item.children.length > 0 && (
            <Box sx={{ ml: depth * 3 }}>
              {renderItems(item.children, depth + 1)}
            </Box>
          )}
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        border: `1px solid ${neutral[200]}`,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header with search bar */}
      <Box
        sx={{
          p: 5,
          borderBottom: `1px solid ${neutral[200]}`,
        }}
      >
        <QuickSearch
          id='toc-search'
          placeholder={t('groups.manuals.exclusions.searchPlaceholder')}
          onChange={setSearchQuery}
          fullWidth
        />
      </Box>

      {/* List section */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Manual (root) item */}
        <Box>
          <ToCItem
            item={tocData}
            isSelected={getItemSelectionState(tocData).isSelected}
            isIndeterminate={getItemSelectionState(tocData).isIndeterminate}
            isExpanded={expandedIds.has(tocData.id)}
            onToggle={handleToggle}
            onExpand={handleExpand}
            disabled={disabled}
          />
        </Box>

        {/* List of items - only show when manual is expanded */}
        {expandedIds.has(tocData.id) && (
          <Box>
            {filteredChildren.length > 0 ? (
              renderItems(filteredChildren, 1)
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  {t('groups.manuals.exclusions.noResults')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ToCSelector;
